import axios from "axios";
import { useDraftStore } from "@/store/draft";
import { useTrackingStore } from "@/store/tracking";
import { useAspspStore } from "@/store/aspsp";
import { useCartStore } from "@/store/cart";
import { constants } from "@/constants";
import Logger from "@/services/logger";
import sha256 from "sha256";
import { isVanguardOnlyPreview } from "@/services/testFixtures";

export default class Orion {
  static getApiBaseUrl() {
    if (process.env.VUE_APP_ORION_API_BASE_URL) {
      return process.env.VUE_APP_ORION_API_BASE_URL;
    }
    if (window.globalConfig.siteBaseUrl) {
      const siteBaseUrl = new URL(window.globalConfig.siteBaseUrl);
      const domainName = siteBaseUrl.host.split(".").slice(-2).join(".");
      return "https://orionv2." + domainName;
    }
  }

  static getOldApiBaseUrl() {
    return process.env.VUE_APP_OLD_ORION_API_BASE_URL;
  }

  static prepareOrder(
    cart,
    newOrder,
    orderExtraData = {},
    isOneClickCompleted = false,
  ) {
    const oldOrder = useDraftStore().order;
    const preparedOrder = Object.assign({}, oldOrder, newOrder, orderExtraData);

    if (newOrder.sameAsBilling) {
      this.copyBillingIntoShipping(preparedOrder);
    }

    const regexIdentifier = /^[a-zA-Z0-9]*$/;
    let walletId = null,
      sfId = null;
    if (
      useTrackingStore().urlParams.walletId &&
      regexIdentifier.test(useTrackingStore().urlParams.walletId)
    ) {
      walletId = useTrackingStore().urlParams.walletId;
    }
    if (
      useTrackingStore().urlParams.sfId &&
      regexIdentifier.test(useTrackingStore().urlParams.sfId)
    ) {
      sfId = useTrackingStore().urlParams.sfId;
    }

    preparedOrder.status =
      isOneClickCompleted || preparedOrder.status === "completed"
        ? "completed"
        : "pending"; // only palyance will change status to completed
    preparedOrder.url = window.location.href;
    preparedOrder.vanguardRevision = `shop-${process.env.VUE_APP_BUILD_DATE}`;
    preparedOrder.testLevel = useTrackingStore().urlParams.testLevel || null;
    preparedOrder.trackingCode =
      useTrackingStore().urlParams.trackingCode ||
      useTrackingStore().urlParams.source ||
      "XMISSING";
    preparedOrder.leadTrackingCode =
      useTrackingStore().urlParams.leadTrackingCode || "";
    preparedOrder.affiliatePicklist = window.globalConfig.affiliateId;
    preparedOrder.contactPolarisId = sfId;
    preparedOrder.currencyIsoCode = window.globalConfig.currencyIsoCode;
    preparedOrder.palyancePaymentId = useAspspStore().paymentIdentifier || "";
    preparedOrder.palyancePaymentAccountIdentifier =
      useAspspStore().paymentAccountIdentifier || null;
    preparedOrder.walletId = walletId;
    preparedOrder.aspsp =
      (useAspspStore().isAspspEligibleSepa &&
        useDraftStore().selectedPaymentMethod ===
          constants.PAYMENT_METHOD_SEPA) ||
      (useAspspStore().isAspspEligibleBankCard &&
        useDraftStore().selectedPaymentMethod ===
          constants.PAYMENT_METHOD_BANK_CARD);
    preparedOrder.paymentType = useDraftStore().selectedPaymentMethod;
    preparedOrder.emailContentId = window.globalConfig.emailContentId;
    preparedOrder.abandonEmailContentId =
      window.globalConfig.abandonEmailContentId;
    const isOrderPage = window.globalConfig.isOrderPage;
    preparedOrder.formCode = isOrderPage
      ? window.globalConfig.orderPageSlug || "ORDERPAGE"
      : "SHOP";
    preparedOrder.domainId = window.globalConfig.domainId;
    preparedOrder.mindbazBase = useTrackingStore().urlParams.base || "";
    preparedOrder.mindbazCampaign =
      useTrackingStore().urlParams.campaignId || "";
    preparedOrder.mindbazShoot = useTrackingStore().urlParams.shootId || "";
    preparedOrder.mindbazSegment = useTrackingStore().urlParams.segmentId || "";
    preparedOrder.marketingCloudDataSourceName =
      useTrackingStore().urlParams.d || "";
    preparedOrder.marketingCloudJobId = useTrackingStore().urlParams.j || "";
    preparedOrder.marketingCloudListId = useTrackingStore().urlParams.l || "";
    preparedOrder.marketingCloudBatchId = useTrackingStore().urlParams.b || "";
    preparedOrder.marketingCloudSendDate =
      useTrackingStore().urlParams.senddate || "";
    preparedOrder.source = isOrderPage ? "orderpage" : "shop";
    preparedOrder.lang = window.globalConfig.lang;
    preparedOrder.externalPartnerId =
      useTrackingStore().urlParams.externalPartnerId;
    preparedOrder.isMultipleChoice = true;
    preparedOrder.screenshotBdc = null;
    preparedOrder.isPalyanceEligible = useAspspStore().isEligible;
    preparedOrder.has1ClickOptOut = useDraftStore().has1ClickOptOut;
    preparedOrder.billingEntity = useDraftStore().billingEntityId;
    preparedOrder.orderMethod = this.getOrderMethod(preparedOrder);
    preparedOrder.shippingPrice = cart.shippingPrice / 100;
    preparedOrder.queryStringParameters = useTrackingStore().urlParams;
    preparedOrder.useDataFromChoices = window.globalConfig.useDataFromChoices
      ? window.globalConfig.useDataFromChoices
      : false;
    preparedOrder.linkRedirects = (window.productDetails?.linkRedirects || [])
      .filter((r) => {
        if (preparedOrder.orderType === "crosssell") {
          return r.crossSellStep != null;
        }
        return r.crossSellStep == null;
      })
      .map((r) => ({
        label: r.label || "",
        url: r.url || "",
        order: r.order || 0,
        crossSellStep: r.crossSellStep ?? null,
      }));

    return preparedOrder;
  }

  static needCopyBillingIntoShipping(order) {
    return (
      order.shippingGender !== order.billingGender ||
      order.shippingFirstName !== order.billingFirstName ||
      order.shippingLastName !== order.billingLastName ||
      order.shippingPhoneNumber !== order.billingPhoneNumber ||
      order.shippingStreet3 !== order.billingStreet3 ||
      order.shippingStreet4 !== order.billingStreet4 ||
      order.shippingStreet1 !== order.billingStreet1 ||
      order.shippingStreet2 !== order.billingStreet2 ||
      order.shippingZipCode !== order.billingZipCode ||
      order.shippingCity !== order.billingCity ||
      order.shippingCountry !== order.billingCountry
    );
  }

  static copyBillingIntoShipping(order) {
    order.shippingGender = order.billingGender;
    order.shippingFirstName = order.billingFirstName;
    order.shippingLastName = order.billingLastName;
    order.shippingPhoneNumber = order.billingPhoneNumber;
    order.shippingStreet3 = order.billingStreet3;
    order.shippingStreet4 = order.billingStreet4;
    order.shippingStreet1 = order.billingStreet1;
    order.shippingStreet2 = order.billingStreet2;
    order.shippingZipCode = order.billingZipCode;
    order.shippingCity = order.billingCity;
    order.shippingCountry = order.billingCountry;
  }

  static sendOrder(cart, order, domainId, actionLegacy = "create") {
    if (isVanguardOnlyPreview()) {
      Logger.log("sendOrder skipped (vanguard_only preview)", {
        cart,
        order: {
          externalOrderId: order?.externalOrderId,
          testLevel: order?.testLevel || "vanguard_only",
        },
        actionLegacy,
      });
      return Promise.resolve({
        data: {
          preview: true,
          externalOrderId: order?.externalOrderId || null,
        },
        status: 200,
      });
    }

    return new Promise((resolve, reject) => {
      axios
        .post(`${this.getApiBaseUrl()}/order-v2`, {
          cart,
          order,
          domainId,
          actionLegacy,
        })
        .then(
          (response) => {
            console.log(response);
            Logger.log("sendOrder response", {
              cart,
              order: {
                externalOrderId: order.externalOrderId,
                billingEmail: sha256(order.billingEmail.toLowerCase()),
                trackingCode: order.trackingCode,
                testLevel: order.testLevel,
                queryStringParameters: order.queryStringParameters,
              },
              status: response.status,
              actionLegacy: actionLegacy,
            });
            resolve(response);
          },
          (error) => {
            console.error(error);
            Logger.error("sendOrder error", error);
            reject(error);
          },
        );
    });
  }

  static updateContactOnOrder(contact) {
    return new Promise((resolve, reject) => {
      axios
        .post(`${this.getApiBaseUrl()}/order-v2`, {
          contact,
        })
        .then(
          (response) => {
            console.log(response);
            Logger.log("updateContactOnOrder response", response);
            resolve(response);
          },
          (error) => {
            console.error(error);
            Logger.log("updateContactOnOrder error", error);
            reject(error);
          },
        );
    });
  }

  static cancelOrder(externalOrderId) {
    return new Promise((resolve, reject) => {
      axios
        .post(`${this.getApiBaseUrl()}/cancel`, {
          order: { externalOrderId },
        })
        .then(
          (response) => {
            console.log(response);
            Logger.log("cancelOrder response", {
              externalOrderId,
              status: response.status,
            });
            resolve(response);
          },
          (error) => {
            console.error(error);
            Logger.error("cancelOrder error", error);
            reject(error);
          },
        );
    });
  }

  static async getAlreadyOrdered(
    productId = "",
    sfId = "",
    email = "",
    insert = false,
  ) {
    const url = `${this.getOldApiBaseUrl()}/already`;
    const params = {
      productId,
      sfId,
      email,
    };

    if (insert) {
      params.insert = "1";
    }

    try {
      const response = await axios.get(url, { params });
      Logger.log("getAlreadyOrdered response", {
        productId,
        status: response.data?.status,
      });
      return response.data;
    } catch (error) {
      Logger.log("getAlreadyOrdered error", error);
      throw error;
    }
  }

  static is1ClickLinkAlreadyUsed() {
    return new Promise((resolve, reject) => {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get("testAlready") === "true") {
        return resolve({ status: true });
      }

      const url = `${this.getOldApiBaseUrl()}/alreadyclicked`;
      const cartItem = useCartStore().cart?.items?.[0];
      const params = {
        trackingCode: useTrackingStore().urlParams.trackingCode,
        sfId: useTrackingStore().urlParams.sfId,
        formCode: `${window.globalConfig.isOrderPage ? window.globalConfig.orderPageSlug || "ORDERPAGE" : "SHOP"}-${window.globalConfig.domainId}`,
        paymentType: useDraftStore().selectedPaymentMethod,
        choiceId: cartItem?.choice?.choiceId,
        productId: cartItem?.choice?.salesforceProductId,
      };
      axios.get(url, { params }).then(
        (response) => {
          console.log(response);
          Logger.log("is1ClickLinkAlreadyUsed response", response);
          resolve(response.data);
        },
        (error) => {
          console.error(error);
          Logger.log("is1ClickLinkAlreadyUsed error", error);
          reject(error);
        },
      );
    });
  }
  static getOrderMethod(order) {
    // Priority 1: Fallback 1-click
    if (order.externalPartnerId === "fallback1c") {
      return "fallback_oneclick";
    }

    // Priority 2: 1-click
    if (
      useDraftStore().isEligibleToOneClick &&
      window.globalConfig.authorized1click
    ) {
      return "oneclick";
    }

    // Priority 3: Simple prefill
    if (
      useAspspStore().prefillData &&
      useAspspStore().prefillData.EMAIL &&
      !order.walletId
    ) {
      return "prefill";
    }

    // Priority 4: Credit Card prefill
    if (
      useAspspStore().prefillData &&
      order.walletId &&
      order.palyancePaymentAccountIdentifier &&
      order.paymentType === constants.PAYMENT_METHOD_BANK_CARD
    ) {
      return "palyance_prefill_cc";
    }

    // Priority 5: Palyance SEPA
    if (order.paymentType === constants.PAYMENT_METHOD_SEPA && order.walletId) {
      if (useAspspStore().aspspPaymentInformations?.sepa?.doubleOptin) {
        return "palyance_with_validation";
      }
      return "palyance_without_validation";
    }

    // Default case
    return "payment_form";
  }
}
